import isNil from 'lodash/isNil';
import API from './../api';
import {
    DASHBOARD_DASHBOARD_BY_ID_ENDPOINT,
    DASHBOARD_DASHBOARD_DEFAULT_ENDPOINT,
    DASHBOARD_DASHBOARD_TAB_DETAIL_ENDPOINT,
    DASHBOARD_DASHBOARD_TAB_LIST_ENDPOINT,
    DASHBOARD_DASHBOARD_TAB_WIDGET_LIST_ENDPOINT,
    DASHBOARD_DASHBOARD_TAB_WIDGET_BULK_UPDATE_ENDPOINT,
    DASHBOARD_DASHBOARD_TAB_WIDGET_DETAIL_ENDPOINT,
} from '../endpoints';
import { getWidgetMinHeightBySysname, generateWidgetID } from '../components/dashboard/widgets/helpers';

export default {
    namespaced: true,

    state() {
        return {
            currentDashboard: null,
            currentDashboardTab: null,
            currentDashboardTabWidgets: null,
            currentDashboardTabs: [],
            isDraggingWidget: false,
            isResizingWidget: false,
            refreshedWidgetSysname: '',
        };
    },

    getters: {
        currentDashboard: state => state.currentDashboard,
        currentDashboardTab: state => state.currentDashboardTab,
        currentDashboardTabWidgets: state => state.currentDashboardTabWidgets,
        currentDashboardTabWidgetsNullable: state => isNil(state.currentDashboardTabWidgets),
        currentDashboardTabs: state => state.currentDashboardTabs,
        currentDashboardTabNewWidgetY: state => {
            const lowestCurrentDashboardTabWidget = (state.currentDashboardTabWidgets || [])
                .reduce((latest, current) => {
                    if (!latest) {
                        return current;
                    }
                    return latest.y + latest.h >= current.y + current.h ? latest : current;
                }, null);
            return lowestCurrentDashboardTabWidget && lowestCurrentDashboardTabWidget.y + lowestCurrentDashboardTabWidget.h || 0;
        },
        dashboardTabByID: state => id =>
            state.currentDashboardTabs.find(item => item.id === id),
        isDraggingWidget: state => state.isDraggingWidget,
        isResizingWidget: state => state.isResizingWidget,
        refreshedWidgetSysname: state => state.refreshedWidgetSysname,
    },

    mutations: {
        assignedCurrentDashboardTab(state, { id }) {
            state.currentDashboardTab = state.currentDashboardTabs.find(
                item => item.id === id
            );
        },
        assignedFirstCurrentDashboardTab(state) {
            state.currentDashboardTab = state.currentDashboardTabs[0];
        },
        clearedCurrentDashboard(state) {
            state.currentDashboard = null;
            state.currentDashboardTabWidgets = null;
            state.currentDashboardTabs = [];
        },
        clearedCurrentDashboardTabWidgets(state) {
            state.currentDashboardTabWidgets = null;
        },
        gotCurrentDashboard(state, response) {
            state.currentDashboard = response.data;
        },
        gotCurrentDashboardTabs(state, response) {
            state.currentDashboardTabs = response.data;
        },
        gotCurrentDashboardTabWidgets(state, response) {
            state.currentDashboardTabWidgets = response.data;
        },
        gotUpdatedCurrentDashboardTabWidget(state, response) {
            if (!isNil(state.currentDashboardTabWidgets)) {
                state.currentDashboardTabWidgets = state.currentDashboardTabWidgets.map(
                    item => (item.id === response.data.id ? response.data : item)
                );
            }
        },
        createdDashboardTab(state, tab) {
            state.currentDashboardTabs.push(tab);
            state.currentDashboardTab = tab;
            state.currentDashboardTabWidgets = [];
        },
        updatedCurrentDashboardTabWidgets(state, tabWidgets) {
            state.currentDashboardTabWidgets = tabWidgets;
            state.currentDashboardTabWidgets.forEach(tabWidget => {
                if (tabWidget.x < 0) {
                    tabWidget.x = 0;
                }
                if (tabWidget.y < 0) {
                    tabWidget.y = 0;
                }
            });
        },
        deletedDashboardTab(state, { id }) {
            state.currentDashboardTabs = state.currentDashboardTabs.filter(
                item => item.id !== id
            );
        },
        deletedDashboardTabWidget(state, { id }) {
            if (!isNil(state.currentDashboardTabWidgets)) {
                state.currentDashboardTabWidgets = state.currentDashboardTabWidgets.filter(
                    item => item.id !== id
                );
            }
        },
        setIsDraggingWidget(state, isDraggingWidget) {
            state.isDraggingWidget = isDraggingWidget;
        },
        setIsResizingWidget(state, isResizingWidget) {
            state.isResizingWidget = isResizingWidget;
        },
        setRefreshedWidgetSysname(state, sysname) {
            state.refreshedWidgetSysname = sysname
        }
    },

    actions: {
        async fetchCurrentDashboard({ commit, dispatch, state }, { id } = {}) {
            const endpoint = !!id
                ? DASHBOARD_DASHBOARD_BY_ID_ENDPOINT(id)
                : DASHBOARD_DASHBOARD_DEFAULT_ENDPOINT();
            commit('clearedCurrentDashboard');
            commit('gotCurrentDashboard', await API.get(endpoint));
            if (state.currentDashboard) {
                await dispatch('fetchCurrentDashboardTabs');
            }
        },
        async fetchCurrentDashboardTabs({ commit, state }) {
            commit(
                'gotCurrentDashboardTabs',
                await API.get(DASHBOARD_DASHBOARD_TAB_LIST_ENDPOINT(), {
                    params: { dashboard_id: state.currentDashboard.id },
                })
            );
        },
        async fetchCurrentDashboardTabWidgets({ commit, state }) {
            commit(
                'gotCurrentDashboardTabWidgets',
                await API.get(DASHBOARD_DASHBOARD_TAB_WIDGET_LIST_ENDPOINT(), {
                    params: { dashboard_tab_id: state.currentDashboardTab.id },
                })
            );
        },
        async setCurrentDashboardTab({ commit, dispatch, state }, { id }) {
            commit('clearedCurrentDashboardTabWidgets');
            commit('assignedCurrentDashboardTab', { id });
            if (state.currentDashboardTab) {
                await dispatch('fetchCurrentDashboardTabWidgets');
            }
        },
        async setFirstCurrentDashboardTab({ commit, dispatch, state }) {
            commit('clearedCurrentDashboardTabWidgets');
            commit('assignedFirstCurrentDashboardTab');
            if (state.currentDashboardTab) {
                await dispatch('fetchCurrentDashboardTabWidgets');
            }
        },
        async deleteDashboardTab({ commit, dispatch, state }, { id }) {
            await API.delete(DASHBOARD_DASHBOARD_TAB_DETAIL_ENDPOINT(id));
            commit('deletedDashboardTab', { id });
            if (state.currentDashboardTab.id === id) {
                await dispatch('setFirstCurrentDashboardTab');
            }
        },
        async deleteDashboardTabWidget({ commit }, { id }) {
            await API.delete(
                DASHBOARD_DASHBOARD_TAB_WIDGET_DETAIL_ENDPOINT(id)
            );
            commit('deletedDashboardTabWidget', { id });
        },
        async updateDashboardTabWidgets(_, tabWidgets) {
            await API.patch(
                DASHBOARD_DASHBOARD_TAB_WIDGET_BULK_UPDATE_ENDPOINT(),
                tabWidgets.map(tabWidget => ({
                    id: tabWidget.id,
                    x: tabWidget.x,
                    y: tabWidget.y,
                    w: tabWidget.w,
                    h: tabWidget.h,
                    i: tabWidget.i,
                }))
            );
        },
        async createDashboardTabWidget({ getters, dispatch }, { sysname }) {
            await API.post(DASHBOARD_DASHBOARD_TAB_WIDGET_LIST_ENDPOINT(), {
                tab_id: getters.currentDashboardTab.id,
                widget_sysname: sysname,
                x: 0,
                y: getters.currentDashboardTabNewWidgetY,
                h: getWidgetMinHeightBySysname(sysname),
                w: 12,
                i: generateWidgetID(),
            });
            await dispatch('fetchCurrentDashboardTabWidgets');
        },
        async updateDashboardTabWidgetSettings({ commit }, { id, ...payload }) {
            commit(
                'gotUpdatedCurrentDashboardTabWidget',
                await API.patch(
                    DASHBOARD_DASHBOARD_TAB_WIDGET_DETAIL_ENDPOINT(id),
                    payload
                )
            );
        },
    },
};
